/**
 * Ajustes → Plantillas de email: mensajes habituales para escribir a un cliente desde su ficha
 * (pedir documentación, recordar una cita o un pago…). Con variables como {{cliente.displayName}}
 * que se rellenan al usarlas. ADMIN y ABOGADO las editan; el resto las ve.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, Loader2, Mail, Plus, Save, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import type { EmailTemplateDto, EmailTemplateListResponse } from '@toga/shared'

const inputCls =
  'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

const STARTER_BODY = `Estimado/a {{cliente.displayName}}:



Un cordial saludo,`

type Field = 'subject' | 'body'

export default function EmailTemplatesSettingsPage() {
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')

  const [items, setItems] = useState<EmailTemplateDto[]>([])
  const [variables, setVariables] = useState<EmailTemplateListResponse['variables']>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState(STARTER_BODY)
  const [saving, setSaving] = useState(false)
  const [lastField, setLastField] = useState<Field>('body')
  const subjectRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const r = await api.get<EmailTemplateListResponse>('/email-templates')
      setItems(r.items)
      setVariables(r.variables)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId]
  )

  useEffect(() => {
    if (selected) {
      setName(selected.name)
      setSubject(selected.subject)
      setBody(selected.body)
    }
  }, [selected])

  const onNew = () => {
    setSelectedId(null)
    setName('')
    setSubject('')
    setBody(STARTER_BODY)
    setError(null)
  }

  const apiError = (err: unknown): string => {
    if (err instanceof ApiError) {
      const b = err.body as
        | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
        | undefined
      const first = b?.issues?.fieldErrors
        ? Object.values(b.issues.fieldErrors).flat().find(Boolean)
        : undefined
      return first ?? b?.error ?? 'No se pudo guardar'
    }
    return 'Error de red'
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const payload = { name: name.trim(), subject: subject.trim(), body: body.trim() }
      const r = selectedId
        ? await api.patch<{ template: EmailTemplateDto }>(`/email-templates/${selectedId}`, payload)
        : await api.post<{ template: EmailTemplateDto }>('/email-templates', payload)
      await load()
      setSelectedId(r.template.id)
    } catch (err) {
      setError(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    if (!selectedId || !confirm('¿Eliminar esta plantilla?')) return
    try {
      await api.delete(`/email-templates/${selectedId}`)
      onNew()
      await load()
    } catch (err) {
      setError(apiError(err))
    }
  }

  /** Inserta {{clave}} donde está el cursor del asunto o del cuerpo (el último que se tocó). */
  const insertVariable = (key: string) => {
    const token = `{{${key}}}`
    const el = lastField === 'subject' ? subjectRef.current : bodyRef.current
    const value = lastField === 'subject' ? subject : body
    const set = lastField === 'subject' ? setSubject : setBody
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    set(value.slice(0, start) + token + value.slice(end))
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">Plantillas de email</h2>
          <p className="t-muted text-sm mt-1">
            Mensajes habituales para escribir a un cliente desde su ficha. Las variables se rellenan
            solas al usarlas; si falta alguna, Toga lo avisa antes de enviar.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={onNew}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> Nueva plantilla
          </button>
        )}
      </header>

      {error && (
        <div
          role="alert"
          className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2"
        >
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[220px_1fr_240px] gap-4">
        <aside className="surface p-2 overflow-y-auto max-h-[640px]">
          {loading ? (
            <div className="t-faint text-xs flex items-center gap-1.5 py-2">
              <Loader2 size={11} className="animate-spin" /> Cargando…
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs t-faint p-2">Aún no hay plantillas.</p>
          ) : (
            <ul className="space-y-0.5">
              {items.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={[
                      'w-full text-left px-2 py-1.5 rounded text-sm border',
                      t.id === selectedId
                        ? 'bg-toga-500/15 text-toga-200 border-toga-500/30'
                        : 't-secondary hover:bg-hover border-transparent',
                    ].join(' ')}
                  >
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-[10px] t-faint truncate">{t.subject}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <form onSubmit={onSubmit} className="surface p-4 space-y-3">
          <label className="text-xs t-muted block">
            Nombre
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className={`${inputCls} mt-1 font-semibold`}
              placeholder="Ej.: Recordatorio de cita"
              required
              disabled={!canWrite}
            />
          </label>
          <label className="text-xs t-muted block">
            Asunto
            <input
              ref={subjectRef}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setLastField('subject')}
              maxLength={200}
              className={`${inputCls} mt-1`}
              required
              disabled={!canWrite}
            />
          </label>
          <label className="text-xs t-muted block">
            Mensaje
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => setLastField('body')}
              rows={14}
              maxLength={10000}
              className={`${inputCls} mt-1`}
              required
              disabled={!canWrite}
            />
          </label>
          <p className="text-[11px] t-faint">
            Tu firma se añade sola al final al enviar. Lo que solo sabes tú (fechas, importes…)
            déjalo en blanco, p. ej. «______», para completarlo al usarla.
          </p>

          {canWrite && (
            <div className="flex justify-between items-center pt-1">
              {selectedId ? (
                <button
                  type="button"
                  onClick={() => void onDelete()}
                  className="px-3 py-1.5 rounded-lg text-sm text-rose-300 border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/15 flex items-center gap-1.5"
                >
                  <Trash2 size={13} /> Eliminar
                </button>
              ) : (
                <span />
              )}
              <button
                type="submit"
                disabled={saving}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {selectedId ? 'Guardar cambios' : 'Crear plantilla'}
              </button>
            </div>
          )}
        </form>

        <aside className="surface p-3 overflow-y-auto max-h-[640px]">
          <h3 className="text-sm font-semibold t-primary mb-2 flex items-center gap-2">
            <Mail size={14} className="text-toga-300" /> Variables
          </h3>
          <p className="text-[10px] t-faint mb-2">
            {canWrite
              ? `Pulsa una para insertarla en el ${lastField === 'subject' ? 'asunto' : 'mensaje'}.`
              : 'Se rellenan al usar la plantilla.'}
          </p>
          <ul className="space-y-0.5 text-xs">
            {variables.map((v) => (
              <li key={v.key}>
                <button
                  type="button"
                  disabled={!canWrite}
                  onClick={() => insertVariable(v.key)}
                  className="w-full text-left px-2 py-1 rounded hover:bg-hover disabled:hover:bg-transparent"
                  title={v.description}
                >
                  <span className="font-mono text-toga-300 break-all">{`{{${v.key}}}`}</span>
                  <span className="block text-[10px] t-faint">{v.description}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  )
}
